import React, { useState } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaDownload, FaFilter, FaSearch } from 'react-icons/fa';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';

const EventsCalendar = () => {
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewType, setViewType] = useState('calendar');

  // Dados simulados para os eventos
  const events = [
    {
      id: 1,
      title: "ExpoConstrução 2025",
      description: "O maior evento de construção civil do país. Venha conhecer as últimas tendências e inovações do mercado.",
      location: "Expo Center Norte, São Paulo - SP",
      start: "2025-08-15",
      end: "2025-08-19", // +1 dia para a visualização no calendário
      category: "Construção",
      status: "open",
      exhibitors: 312,
      expectedVisitors: 25000,
      backgroundColor: "#0d6efd", // azul para Construção
      borderColor: "#0d6efd",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1541976590-713941681591?q=80&w=2940&auto=format&fit=crop"
      }
    },
    {
      id: 2,
      title: "Feira de Tecnologia Industrial",
      description: "Evento focado em automação e tecnologia para o setor industrial. Soluções inovadoras para aumentar a eficiência da sua empresa.",
      location: "Centro de Convenções, Belo Horizonte - MG",
      start: "2025-09-10",
      end: "2025-09-14", // +1 dia para a visualização no calendário
      category: "Tecnologia",
      status: "open",
      exhibitors: 185,
      expectedVisitors: 15000,
      backgroundColor: "#6610f2", // roxo para Tecnologia
      borderColor: "#6610f2",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1538474705339-e87de81450e8?q=80&w=2940&auto=format&fit=crop"
      }
    },
    {
      id: 3,
      title: "Congresso de Agronegócio",
      description: "Evento dedicado às inovações e tendências do agronegócio brasileiro. Painéis, workshops e área de exposição.",
      location: "Centro de Eventos, Ribeirão Preto - SP",
      start: "2025-10-05",
      end: "2025-10-09", // +1 dia para a visualização no calendário
      category: "Agronegócio",
      status: "open",
      exhibitors: 230,
      expectedVisitors: 18000,
      backgroundColor: "#198754", // verde para Agronegócio
      borderColor: "#198754",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1495107334309-fcf20504a5ab?q=80&w=2940&auto=format&fit=crop"
      }
    },
    {
      id: 4,
      title: "Feira Internacional de Alimentos",
      description: "O maior evento do setor alimentício da América Latina. Oportunidade para apresentar seus produtos para um público internacional.",
      location: "Anhembi, São Paulo - SP",
      start: "2025-11-20",
      end: "2025-11-25", // +1 dia para a visualização no calendário
      category: "Alimentação",
      status: "pending",
      exhibitors: 0,
      expectedVisitors: 30000,
      backgroundColor: "#ffc107", // amarelo para Alimentação
      borderColor: "#ffc107",
      textColor: "#000",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1498654896293-37aacf113fd9?q=80&w=2940&auto=format&fit=crop"
      }
    },
    {
      id: 101,
      title: "Congresso de Energia Sustentável",
      description: "Evento dedicado às energias renováveis e sustentabilidade. Painéis, workshops e área de exposição.",
      location: "Centro de Convenções, Rio de Janeiro - RJ",
      start: "2025-03-10",
      end: "2025-03-14", // +1 dia para a visualização no calendário
      category: "Energia",
      status: "completed",
      exhibitors: 178,
      visitors: 12450,
      backgroundColor: "#6c757d", // cinza para eventos passados
      borderColor: "#6c757d",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1497440001374-f26997328c1b?q=80&w=2940&auto=format&fit=crop"
      }
    },
    {
      id: 102,
      title: "Feira de Mobiliário e Design",
      description: "O maior evento do setor de móveis e design de interiores do Brasil.",
      location: "Expo Center Norte, São Paulo - SP",
      start: "2025-04-22",
      end: "2025-04-27", // +1 dia para a visualização no calendário
      category: "Design",
      status: "completed",
      exhibitors: 245,
      visitors: 23780,
      backgroundColor: "#6c757d", // cinza para eventos passados
      borderColor: "#6c757d",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1540932239986-30128078f3c5?q=80&w=2940&auto=format&fit=crop"
      }
    },
    {
      id: 5,
      title: "Feira de Equipamentos Médicos",
      description: "Principais inovações em equipamentos e tecnologias médicas.",
      location: "Expo Center Norte, São Paulo - SP",
      start: "2026-01-15",
      end: "2026-01-19", // +1 dia para a visualização no calendário
      category: "Saúde",
      status: "planned",
      exhibitors: 0,
      expectedVisitors: 15000,
      backgroundColor: "#dc3545", // vermelho para Saúde
      borderColor: "#dc3545",
      extendedProps: {
        image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?q=80&w=2940&auto=format&fit=crop"
      }
    }
  ];

  // Formatação de data
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('pt-BR', options);
  };

  // Formatação de números
  const formatNumber = (number) => {
    return new Intl.NumberFormat('pt-BR').format(number);
  };

  // Filtrar eventos por categoria
  const filteredEvents = events.filter(event => {
    if (filterCategory === 'all') return true;
    return event.category === filterCategory;
  });
  
  // Obter todas as categorias únicas
  const categories = [...new Set(events.map(event => event.category))];

  // Manipular clique no evento
  const handleEventClick = (clickInfo) => {
    const eventId = parseInt(clickInfo.event.id);
    const eventData = events.find(e => e.id === eventId);
    setSelectedEvent(eventData);
  };

  // Voltar para a lista de eventos
  const goBackToEvents = () => {
    navigate('/dashboard/events');
  };

  // Navegar para detalhes do evento
  const goToEventDetails = (eventId) => {
    navigate(`/dashboard/events/${eventId}`);
  };

  // Registrar para evento
  const registerForEvent = (eventId) => {
    navigate(`/dashboard/events/${eventId}/register`);
  };

  // Alternar exibição entre calendário e lista
  const toggleView = (type) => {
    setViewType(type);
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            className="me-3"
            onClick={goBackToEvents}
          >
            <FaArrowLeft /> Voltar
          </Button>
          <h1 className="mb-0">Calendário de Eventos</h1>
        </div>

        <div>
          <Button 
            variant={viewType === 'calendar' ? 'primary' : 'outline-primary'} 
            className="me-2"
            onClick={() => toggleView('calendar')}
          >
            <FaCalendarAlt className="me-2" />
            Calendário
          </Button>
          <Button 
            variant={viewType === 'list' ? 'primary' : 'outline-primary'}
            onClick={() => toggleView('list')}
          >
            <FaFilter className="me-2" />
            Lista
          </Button>
        </div>
      </div>
      
      <Row>
        <Col lg={selectedEvent ? 8 : 12}>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Eventos Programados</h5>
              <div>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter /> Filtros
                </Button>
                <Button 
                  variant="outline-success" 
                  size="sm"
                  onClick={() => alert('Calendário exportado')}
                >
                  <FaDownload /> Exportar
                </Button>
              </div>
            </Card.Header>
            
            {showFilters && (
              <Card.Body className="border-bottom">
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Filtrar por categoria</Form.Label>
                      <Form.Select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        <option value="all">Todas as categorias</option>
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Buscar eventos</Form.Label>
                      <div className="d-flex">
                        <Form.Control
                          type="text"
                          placeholder="Buscar por nome ou descrição"
                        />
                        <Button variant="dark" className="ms-2">
                          <FaSearch />
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            )}
            
            <Card.Body>
              {viewType === 'calendar' ? (
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,dayGridYear'
                  }}
                  events={filteredEvents}
                  eventClick={handleEventClick}
                  height="auto"
                  aspectRatio={1.8}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    meridiem: false
                  }}
                  locale={ptBrLocale}
                  buttonText={{
                    today: 'Hoje',
                    month: 'Mês',
                    year: 'Ano'
                  }}
                />
              ) : (
                <div className="event-list">
                  {filteredEvents.length === 0 ? (
                    <Alert variant="info">Nenhum evento encontrado com os filtros atuais.</Alert>
                  ) : (
                    filteredEvents.map(event => (
                      <Card key={event.id} className="mb-3 event-list-item">
                        <Card.Body className="p-3">
                          <Row>
                            <Col md={2} className="d-flex align-items-center justify-content-center">
                              <div className="text-center event-date">
                                <h3 className="mb-0">{new Date(event.start).getDate()}</h3>
                                <span>{new Date(event.start).toLocaleString('pt-BR', { month: 'short' }).toUpperCase()}</span>
                                <small className="d-block">{new Date(event.start).getFullYear()}</small>
                              </div>
                            </Col>
                            <Col md={7}>
                              <Badge bg={event.status === 'completed' ? 'secondary' : 'primary'} className="mb-2">
                                {event.category}
                              </Badge>
                              <h5>{event.title}</h5>
                              <div className="d-flex align-items-center text-muted small">
                                <FaCalendarAlt className="me-1" />
                                <span className="me-3">{formatDate(event.start)} a {formatDate(new Date(event.end).getTime() - 86400000)}</span>
                                <FaMapMarkerAlt className="me-1" />
                                <span>{event.location}</span>
                              </div>
                            </Col>
                            <Col md={3} className="d-flex align-items-center justify-content-end">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                className="me-2"
                                onClick={() => goToEventDetails(event.id)}
                              >
                                Detalhes
                              </Button>
                              {event.status === 'open' && (
                                <Button 
                                  variant="success" 
                                  size="sm"
                                  onClick={() => registerForEvent(event.id)}
                                >
                                  Participar
                                </Button>
                              )}
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {selectedEvent && (
          <Col lg={4}>
            <Card className="shadow-sm sticky-top" style={{ top: '1rem' }}>
              <div style={{ height: '200px', overflow: 'hidden' }}>
                <Card.Img 
                  variant="top" 
                  src={selectedEvent.extendedProps.image} 
                  style={{ 
                    objectFit: 'cover', 
                    height: '100%', 
                    width: '100%',
                    filter: selectedEvent.status === 'completed' ? 'grayscale(40%)' : 'none'
                  }}
                />
                {selectedEvent.status === 'completed' && (
                  <div 
                    style={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '200px', 
                      background: 'rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Badge 
                      bg="secondary" 
                      style={{ 
                        fontSize: '1.2rem', 
                        padding: '0.5rem 1rem' 
                      }}
                    >
                      Concluído
                    </Badge>
                  </div>
                )}
              </div>
              
              <Card.Body>
                <div className="mb-2">
                  <Badge 
                    bg={selectedEvent.status === 'completed' ? 'secondary' : 
                        selectedEvent.status === 'pending' ? 'warning' : 
                        selectedEvent.status === 'planned' ? 'info' : 'primary'}
                    text={selectedEvent.status === 'pending' || selectedEvent.status === 'planned' ? 'dark' : 'white'}
                    className="me-2"
                  >
                    {selectedEvent.category}
                  </Badge>
                  
                  {selectedEvent.status === 'pending' && (
                    <Badge bg="warning" text="dark">Inscrições em breve</Badge>
                  )}
                  
                  {selectedEvent.status === 'planned' && (
                    <Badge bg="info" text="dark">Planejado</Badge>
                  )}
                </div>
                
                <h4>{selectedEvent.title}</h4>
                <p className="text-muted">{selectedEvent.description}</p>
                
                <div className="mt-3">
                  <div className="d-flex align-items-center mb-2">
                    <FaCalendarAlt className={`me-2 ${selectedEvent.status === 'completed' ? 'text-secondary' : 'text-primary'}`} />
                    <span>{formatDate(selectedEvent.start)} a {formatDate(new Date(selectedEvent.end).getTime() - 86400000)}</span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <FaMapMarkerAlt className={`me-2 ${selectedEvent.status === 'completed' ? 'text-secondary' : 'text-danger'}`} />
                    <span>{selectedEvent.location}</span>
                  </div>
                  
                  {selectedEvent.status === 'completed' ? (
                    <>
                      <p className="mb-1"><strong>Expositores:</strong> {formatNumber(selectedEvent.exhibitors)}</p>
                      <p className="mb-1"><strong>Visitantes:</strong> {formatNumber(selectedEvent.visitors)}</p>
                    </>
                  ) : (
                    <>
                      <p className="mb-1">
                        <strong>Expositores:</strong> {
                          selectedEvent.status === 'open' ? 
                          formatNumber(selectedEvent.exhibitors) + ' confirmados' : 
                          'Abertura em breve'
                        }
                      </p>
                      <p className="mb-1"><strong>Público esperado:</strong> {formatNumber(selectedEvent.expectedVisitors)} visitantes</p>
                    </>
                  )}
                </div>
                
                <div className="mt-4">
                  {selectedEvent.status === 'completed' ? (
                    <Button 
                      variant="outline-secondary" 
                      className="w-100"
                      onClick={() => goToEventDetails(selectedEvent.id)}
                    >
                      Ver relatório do evento
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="outline-primary" 
                        className="me-2"
                        onClick={() => goToEventDetails(selectedEvent.id)}
                      >
                        Ver detalhes completos
                      </Button>
                      
                      {selectedEvent.status === 'open' && (
                        <Button 
                          variant="success"
                          onClick={() => registerForEvent(selectedEvent.id)}
                        >
                          Participar
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
      
      <Alert variant="info" className="mt-3">
        <strong>Dica:</strong> Clique em um evento no calendário para ver mais detalhes. Para se inscrever em um evento, 
        clique no botão "Participar" e siga as instruções para escolher seu espaço.
      </Alert>
    </Container>
  );
};

export default EventsCalendar;